/**
 * Custom hook for managing audio level data in public recording
 * Simplified: exposes only audioLevel (0-1) for the Canvas-based waveform
 */

import type { PublicRecordingService } from '@/lib/public-recording-service';
import { useEffect, useState } from 'react';

export const usePublicRecordingWaveform = (
  recordingServiceRef: React.MutableRefObject<PublicRecordingService | null>,
  isRecording: boolean,
  isPaused: boolean
) => {
  const [audioLevel, setAudioLevel] = useState(0);

  // Subscribe to audio level changes from the recording service
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (isRecording && !isPaused && recordingServiceRef.current) {
      unsubscribe = recordingServiceRef.current.onAudioLevelChange((level) => {
        setAudioLevel(level);
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isRecording, isPaused, recordingServiceRef]);

  // Reset audio level when not actively recording
  useEffect(() => {
    if (!isRecording || isPaused) {
      setAudioLevel(0);
    }
  }, [isRecording, isPaused]);

  return { audioLevel };
};
